/**
 * SupabaseAuthContext.tsx — Global Supabase Auth state for WorkRoamer.
 *
 * Wraps @supabase/supabase-js auth lifecycle:
 *  - Subscribes to onAuthStateChange so every component sees live session state.
 *  - Registers the access-token getter on the api-client customFetch so every
 *    API call carries Authorization: Bearer <jwt> automatically.
 *  - On SIGNED_IN / TOKEN_REFRESHED: invalidates ALL TanStack Query caches so
 *    every protected route (opportunities, dashboard, events, settings, etc.)
 *    re-fetches with the new Bearer token — not only /auth/me.
 *
 * Architecture rules:
 *  - This context NEVER calls /api/auth/login or /api/auth/register directly.
 *    All auth flows go through Supabase; the api-server provisions internal
 *    user records on first authenticated request via requireAuth middleware.
 *  - The existing cookie-session path in requireAuth continues to work for
 *    legacy sessions — this context adds a new JWT path alongside it.
 *  - `isAuthReady` is false until the initial getSession() resolves. Query
 *    hooks that gate on useAuthEnabled() will not fire before this flag is true,
 *    eliminating the "fired before Bearer token was available" race condition.
 */

import {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
  type ReactNode,
} from 'react';
import type { Session, User, AuthError } from '@supabase/supabase-js';
import { supabase } from '@/lib/supabaseClient';
import { setAuthTokenGetter } from '@workspace/api-client-react';
import { setBearerTokenGetter } from '@/lib/bearerFetchPatch';
import { useQueryClient } from '@tanstack/react-query';
import { getGetMeQueryKey } from '@workspace/api-client-react';
import { bootLogger } from '@/lib/bootLogger';

// ─── Types ───────────────────────────────────────────────────────────────────

export interface SupabaseAuthState {
  /** Supabase session (null = not signed in, undefined = still loading). */
  session: Session | null | undefined;
  /** Supabase user object (convenience alias for session?.user). */
  supabaseUser: User | null;
  /** True while the initial session check is in flight. */
  loading: boolean;
  /**
   * True once the initial getSession() call has resolved (whether or not a
   * session exists). Query hooks use this to avoid firing before auth is known.
   */
  isAuthReady: boolean;

  signIn: (email: string, password: string) => Promise<{ error: AuthError | null }>;
  signUp: (email: string, password: string, displayName?: string) => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<{ error: AuthError | null }>;
  resetPassword: (email: string) => Promise<{ error: AuthError | null }>;
}

// ─── Context ─────────────────────────────────────────────────────────────────

const SupabaseAuthContext = createContext<SupabaseAuthState | null>(null);

// ─── Provider ────────────────────────────────────────────────────────────────

export function SupabaseAuthProvider({ children }: { children: ReactNode }) {
  const queryClient = useQueryClient();
  const [session,     setSession]     = useState<Session | null | undefined>(undefined);
  const [loading,     setLoading]     = useState(true);
  const [isAuthReady, setIsAuthReady] = useState(false);

  // ── Wire api-client token getter ──────────────────────────────────────────
  // The canonical registrations happen at module-load time in supabaseClient.ts
  // (before any React render) so react-query hooks fired on first mount already
  // carry a Bearer token. This effect is a belt-and-suspenders re-registration
  // in case supabaseClient.ts is ever imported after a first render.
  //
  // NO cleanup: setAuthTokenGetter(null) was previously returned here. That
  // cleanup cleared the module-level getter whenever SupabaseAuthContext
  // unmounted (e.g. React StrictMode double-invoke in dev, or conditional
  // wrapping in tests), which silently disabled auth for all subsequent
  // customFetch calls until the next mount cycle. The getter must stay
  // registered for the lifetime of the page — removing the cleanup is correct.
  useEffect(() => {
    // Both token-getter pipelines use the same fresh getSession() call:
    //
    // Pipeline 1 — @workspace/api-client-react customFetch (generated client):
    //   setAuthTokenGetter registers the getter that customFetch calls before
    //   every API request. This has always worked (JWT_VALID on /persistence/*,
    //   /auth/me, /green-day/status).
    //
    // Pipeline 2 — raw window.fetch() → bearerFetchPatch (use-opportunities,
    //   use-events, use-dashboard, use-settings, PATCH /auth/profile, etc.):
    //   supabaseClient.ts registers getBearerToken() via setBearerTokenGetter()
    //   at module-load time. getBearerToken() relies on a _cachedAccessToken
    //   variable that is populated by supabaseClient.ts's onAuthStateChange
    //   listener. In production, that cache consistently stays null, causing
    //   COOKIE_MISSING 401 on every raw-fetch route regardless of timing.
    //   Root cause is still unknown (likely a production-specific Supabase SDK
    //   initialization ordering issue), but the fix is clear: override the
    //   Pipeline 2 getter here with the same fresh getSession() pattern that
    //   has proven reliable for Pipeline 1. This call runs after React mounts
    //   and overwrites supabaseClient.ts's module-load-time registration.
    const freshTokenGetter = async () => {
      const { data } = await supabase.auth.getSession();
      return data.session?.access_token ?? null;
    };
    setAuthTokenGetter(freshTokenGetter);   // Pipeline 1 (generated API client)
    setBearerTokenGetter(freshTokenGetter); // Pipeline 2 (raw window.fetch routes)
  }, []);

  // ── Subscribe to auth state changes ──────────────────────────────────────
  useEffect(() => {
    // Get initial session synchronously if available.
    supabase.auth.getSession().then(({ data }) => {
      const s = data.session;
      console.log(
        `[NOMAD_DEBUG] SupabaseAuth getSession (initial): ` +
        `uid=${s?.user?.id ?? 'null'} ` +
        `email=${s?.user?.email ?? 'null'} ` +
        `hasToken=${!!s?.access_token} ` +
        `tokenExpiry=${s?.expires_at ?? 'n/a'}`,
      );
      // [BOOT] structured logging — Task #38
      bootLogger.record(
        'supabase-init',
        s ? 'ok' : 'skip',
        s ? `uid=${s.user.id.slice(0, 8)} email=${s.user.email ?? 'n/a'}` : 'no session',
      );
      if (s?.access_token) {
        bootLogger.record('jwt-verified', 'ok', `expires=${s.expires_at ?? 'n/a'}`);
      }
      setSession(s ?? null);
      setLoading(false);
      setIsAuthReady(true);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, newSession) => {
        console.log(
          `[NOMAD_DEBUG] SupabaseAuth onAuthStateChange: ` +
          `event=${_event} ` +
          `uid=${newSession?.user?.id ?? 'null'} ` +
          `email=${newSession?.user?.email ?? 'null'} ` +
          `hasToken=${!!newSession?.access_token} ` +
          `emailConfirmed=${newSession?.user?.email_confirmed_at ?? 'null'}`,
        );
        setSession(newSession ?? null);
        setLoading(false);
        setIsAuthReady(true);

        // ── Phase 2: Invalidate ALL protected queries on auth events ──────────
        //
        // Previously only /auth/me was invalidated here. That left every other
        // protected query (opportunities, dashboard, events, settings, etc.) in
        // error state (COOKIE_MISSING 401) for the entire page lifetime because
        // they had fired before the Bearer token was available.
        //
        // Now on SIGNED_IN / TOKEN_REFRESHED we invalidate everything so that
        // all queries refetch with the new token. Queries gated on useAuthEnabled()
        // were never fetched yet (they were disabled), so this triggers their first
        // successful fetch. Queries that errored on an earlier attempt are also
        // cleared and retried.
        if (_event === 'SIGNED_IN' || _event === 'TOKEN_REFRESHED' || (_event === 'INITIAL_SESSION' && !!newSession)) {
          // SIGNED_IN / TOKEN_REFRESHED: user just authenticated or token was refreshed.
          // INITIAL_SESSION (with session): returning user's session was restored from
          //   localStorage. Their queries fired during the startup race window (before
          //   _cachedAccessToken was set) and got COOKIE_MISSING 401. Now that the
          //   onAuthStateChange listener in supabaseClient.ts has set _cachedAccessToken
          //   (it fires before this listener because it was registered at module-load
          //   time, before any React render), invalidating all queries causes them to
          //   refetch with the Bearer token available. This is the fix for the "returning
          //   user sees no opportunities/dashboard/events" bug.
          //
          // [BOOT] structured logging — record SIGNED_IN as supabase-init + jwt if not already done
          if (_event === 'SIGNED_IN' && newSession) {
            bootLogger.record('supabase-init', 'ok',
              `event=SIGNED_IN uid=${newSession.user.id.slice(0, 8)}`);
            bootLogger.record('jwt-verified', 'ok',
              `expires=${newSession.expires_at ?? 'n/a'}`);
          }
          // Invalidate all cached queries — react-query will refetch any that
          // are currently enabled (which they now are, since session is set).
          void queryClient.invalidateQueries();
          console.log(`[NOMAD_DEBUG] SupabaseAuth: invalidated ALL queries after ${_event}`);
        } else if (_event === 'SIGNED_OUT') {
          // Clear all cached data on sign-out to prevent stale data leaking
          // to the next user who logs in on this browser.
          queryClient.clear();
          console.log('[NOMAD_DEBUG] SupabaseAuth: cleared all queries after SIGNED_OUT');
        } else {
          // INITIAL_SESSION with no session (user not signed in), USER_UPDATED, etc.
          // Only refresh /auth/me so Shell can decide whether to redirect to login.
          queryClient.invalidateQueries({ queryKey: getGetMeQueryKey() });
        }
      },
    );

    return () => { subscription.unsubscribe(); };
  }, [queryClient]);

  // ── Auth actions ──────────────────────────────────────────────────────────

  const signIn = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return { error };
  }, []);

  const signUp = useCallback(async (email: string, password: string, displayName?: string) => {
    // Derive the base path at call-time so it works in both dev (base='/') and
    // any future deployment where BASE_URL might change.
    const base = (import.meta.env.BASE_URL as string ?? '/').replace(/\/+$/, '');
    const { error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        data: { display_name: displayName ?? '' },
        // Redirect to the app after email confirmation.
        emailRedirectTo: `${window.location.origin}${base}/auth/callback`,
      },
    });
    return { error };
  }, []);

  const signOut = useCallback(async () => {
    const { error } = await supabase.auth.signOut();
    // Clear TanStack Query cache immediately so stale user data isn't shown.
    queryClient.clear();
    return { error };
  }, [queryClient]);

  const resetPassword = useCallback(async (email: string) => {
    const base = (import.meta.env.BASE_URL as string ?? '/').replace(/\/+$/, '');
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}${base}/auth/reset`,
    });
    return { error };
  }, []);

  return (
    <SupabaseAuthContext.Provider value={{
      session,
      supabaseUser: session?.user ?? null,
      loading,
      isAuthReady,
      signIn,
      signUp,
      signOut,
      resetPassword,
    }}>
      {children}
    </SupabaseAuthContext.Provider>
  );
}

// ─── Hook ────────────────────────────────────────────────────────────────────

export function useSupabaseAuth(): SupabaseAuthState {
  const ctx = useContext(SupabaseAuthContext);
  if (!ctx) throw new Error('useSupabaseAuth must be used inside <SupabaseAuthProvider>');
  return ctx;
}
